import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

// Validation schemas
const emailSchema = z.string().trim().email();
const passwordSchema = z.string().min(6);

const Login = () => {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const { language } = useLanguage();
  const [isSignup, setIsSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({ 
    email: "", 
    password: "", 
    confirmPassword: "",
    fullName: "", 
    company: "",
    phone: ""
  });

  const content = {
    is: {
      login: "Innskráning",
      loginDesc: "Skráðu þig inn á þinn reikning",
      signup: "Nýskráning",
      signupDesc: "Búðu til nýjan reikning",
      email: "Netfang",
      password: "Lykilorð",
      confirmPassword: "Staðfesta lykilorð",
      fullName: "Fullt nafn",
      company: "Fyrirtæki",
      phone: "Símanúmer",
      loginButton: "Skrá inn",
      signupButton: "Nýskrá",
      noAccount: "Ekki með reikning?",
      hasAccount: "Þegar með reikning?",
      signupLink: "Nýskrá hér",
      loginLink: "Skrá inn hér",
      invalidEmail: "Ógilt netfang",
      passwordTooShort: "Lykilorð verður að vera að minnsta kosti 6 stafir",
      passwordsDontMatch: "Lykilorðin eru ekki eins",
      fillAllFields: "Vinsamlegast fylltu út alla reiti",
      checkEmail: "Vinsamlegast athugaðu póstinn þinn til að staðfesta reikninginn",
      // Placeholders
      emailPlaceholder: "nafn@fyrirtaeki.is",
      passwordPlaceholder: "Sláðu inn lykilorð",
      confirmPasswordPlaceholder: "Sláðu inn sama lykilorðið aftur",
      fullNamePlaceholder: "Jón Jónsson",
      companyPlaceholder: "Nafn fyrirtækis",
      phonePlaceholder: "+354 123 4567",
    },
    en: {
      login: "Login",
      loginDesc: "Sign in to your account",
      signup: "Sign Up",
      signupDesc: "Create a new account",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm Password",
      fullName: "Full Name",
      company: "Company",
      phone: "Phone Number",
      loginButton: "Sign In",
      signupButton: "Sign Up",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      signupLink: "Sign up here",
      loginLink: "Sign in here",
      invalidEmail: "Invalid email address",
      passwordTooShort: "Password must be at least 6 characters",
      passwordsDontMatch: "Passwords do not match",
      fillAllFields: "Please fill in all fields",
      checkEmail: "Please check your email to confirm your account",
      // Placeholders
      emailPlaceholder: "name@company.com",
      passwordPlaceholder: "Enter your password",
      confirmPasswordPlaceholder: "Enter password again",
      fullNamePlaceholder: "John Smith",
      companyPlaceholder: "Company name",
      phonePlaceholder: "+1 234 567 8900",
    },
  };

  const t = content[language];

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      emailSchema.parse(loginData.email);
      passwordSchema.parse(loginData.password);

      const { error } = await signIn(loginData.email, loginData.password);
      
      if (error) {
        toast.error(error.message);
      } else {
        navigate("/");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message === "Invalid email" ? t.invalidEmail : t.passwordTooShort);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!signupData.fullName || !signupData.company) {
        toast.error(t.fillAllFields);
        setIsLoading(false);
        return;
      }

      // Check if passwords match
      if (signupData.password !== signupData.confirmPassword) {
        toast.error(t.passwordsDontMatch);
        setIsLoading(false);
        return;
      }

      emailSchema.parse(signupData.email);
      passwordSchema.parse(signupData.password);

      console.log("Attempting signup with:", signupData.email);

      const { data, error } = await signUp(signupData.email, signupData.password);
      
      console.log("Signup response - data:", data, "error:", error);

      if (error) {
        console.error("Signup error:", error);
        toast.error(error.message || "Signup failed");
        setIsLoading(false);
        return;
      }

      if (!data?.user) {
        console.error("No user returned from signup");
        toast.error("Signup failed - no user created");
        setIsLoading(false);
        return;
      }

      // Update profile data after signup (trigger creates the base profile)
      console.log("Updating profile for user:", data.user.id);
      
      // Wait a moment for the trigger to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { error: profileError } = await supabase.from('profiles').update({
        full_name: signupData.fullName,
        company: signupData.company,
        phone: signupData.phone || null,
      }).eq('id', data.user.id);

      if (profileError) {
        console.error("Profile update error:", profileError);
        // Don't fail the signup if profile update fails
        console.warn("Profile update failed, but user was created");
      } else {
        console.log("Profile updated successfully");
      }
      
      toast.success(t.checkEmail);
      setIsSignup(false);
    } catch (error) {
      console.error("Signup catch error:", error);
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message === "Invalid email" ? t.invalidEmail : t.passwordTooShort);
      } else {
        toast.error(String(error) || "An error occurred during signup");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-accent/20 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <Link 
            to={language === "is" ? "/" : "/en"}
            className="flex justify-center hover:opacity-80 transition-opacity"
          >
            <Logo size="lg" variant="gradient" />
          </Link>
          <CardTitle className="text-center">{t.login}</CardTitle>
          <CardDescription className="text-center">{t.loginDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t.emailPlaceholder}
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t.passwordPlaceholder}
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "..." : t.loginButton}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            {t.noAccount}{" "}
            <button
              type="button"
              onClick={() => setIsSignup(true)}
              className="text-primary hover:underline"
            >
              {t.signupLink}
            </button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isSignup} onOpenChange={setIsSignup}>
        <DialogContent>
          <DialogHeader className="space-y-4">
            <Link 
              to={language === "is" ? "/" : "/en"}
              className="flex justify-center hover:opacity-80 transition-opacity"
            >
              <Logo size="lg" variant="gradient" />
            </Link>
            <DialogTitle className="text-center">{t.signup}</DialogTitle>
            <DialogDescription className="text-center">{t.signupDesc}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signup-fullname">{t.fullName}</Label>
              <Input
                id="signup-fullname"
                placeholder={t.fullNamePlaceholder}
                value={signupData.fullName}
                onChange={(e) => setSignupData({ ...signupData, fullName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-company">{t.company}</Label>
              <Input
                id="signup-company"
                placeholder={t.companyPlaceholder}
                value={signupData.company}
                onChange={(e) => setSignupData({ ...signupData, company: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-phone">
                {t.phone} <span className="text-muted-foreground text-sm font-normal">({language === "is" ? "valkvætt" : "optional"})</span>
              </Label>
              <Input
                id="signup-phone"
                type="tel"
                placeholder={t.phonePlaceholder}
                value={signupData.phone}
                onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-email">{t.email}</Label>
              <Input
                id="signup-email"
                type="email"
                placeholder={t.emailPlaceholder}
                value={signupData.email}
                onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">{t.password}</Label>
              <Input
                id="signup-password"
                type="password"
                placeholder={t.passwordPlaceholder}
                value={signupData.password}
                onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-confirm-password">{t.confirmPassword}</Label>
              <Input
                id="signup-confirm-password"
                type="password"
                placeholder={t.confirmPasswordPlaceholder}
                value={signupData.confirmPassword}
                onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "..." : t.signupButton}
            </Button>
            <div className="text-center text-sm">
              {t.hasAccount}{" "}
              <button
                type="button"
                onClick={() => setIsSignup(false)}
                className="text-primary hover:underline"
              >
                {t.loginLink}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;